import React from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import CtaButton from '../../common/CtaButton';
import Price from '../../common/Price';
import { FormattedMessage } from 'react-intl';
import Hyperlink from '../../common/Hyperlink';

const PlanDetailsContainer = styled(Hyperlink)`
  background-color: var(--c-ral);
  border-radius: var(--fr);
  padding: calc(1.25 * var(--fr));
  display: grid;
  grid-template-areas: 'image' 'info' 'button';
  grid-template-rows: calc(7 * var(--fr)) auto calc(5 * var(--fr));
  transition-duration: 0.33s;
  transition-property: background-color, background-color;
  position: relative;

  &:hover {
    background-color: var(--c-charcoal);
  }
`;

const PlanImage = styled.figure`
  grid-area: image;
  background-image: url(${props => props.imageUrl});
  background-size: 200%;
  background-position: 50% 60%;
  background-repeat: no-repeat;
  height: calc(6 * var(--fr));
  position: relative;
  border-radius: calc(0.5 * var(--fr));

  > span {
    position: absolute;
    bottom: calc(var(--fr) / 4);
    left: var(--fr);
    width: 85%;
    font-size: var(--fs-m);
    font-weight: 700;
  }
`;

const PlanInfo = styled.div`
  grid-area: info;
  height: 100%;
`;

const PlanPrice = styled.div`
  font-size: var(--fs-sm);
  font-weight: 500;
  margin-bottom: 1rem;

  div {
    font-size: var(--fs-focus);
    font-weight: 400;
    margin-top: calc(0.25 * var(--fr));
    color: var(--c-manatee);
  }
`;

const PlanObjectives = styled.ul`
  color: var(--c-silver);
  font-size: var(--fs-focus);

  li {
    list-style-type: none;
    padding-left: calc(1.5 * var(--fr));
    position: relative;
  }

  li:not(:last-child) {
    margin-bottom: calc(0.75 * var(--fr));
  }

  li.exclusion {
    color: var(--c-manatee);
  }

  li.benefit::before {
    content: '';
    display: inline-block;
    width: var(--fr);
    height: var(--fr);
    background-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2300a651"><path d="M9 18.632l-5.566-5.567 1.132-1.131 4.434 4.434 10.435-10.434 1.13 1.132z"/></svg>');
    background-size: contain;
    background-repeat: no-repeat;
    margin-right: calc(0.5 * var(--fr));
    margin-left: calc(-1.5 * var(--fr));
    vertical-align: middle;
  }

  li.exclusion::before {
    content: '';
    display: inline-block;
    width: var(--fr);
    height: var(--fr);
    background-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%238f929e"><path d="M19.565 5.566l-1.13-1.132-6.435 6.435-6.434-6.435-1.132 1.132 6.435 6.434-6.435 6.435 1.132 1.13 6.434-6.434 6.435 6.434 1.13-1.13-6.434-6.435z"/></svg>');
    background-size: contain;
    background-repeat: no-repeat;
    margin-right: calc(0.5 * var(--fr));
    margin-left: calc(-1.5 * var(--fr));
    vertical-align: middle;
  }
`;

const PlanButton = styled.div`
  grid-area: button;
  margin-top: calc(1.5 * var(--fr));

  a {
    width: 100%;
  }
`;

const PlanTag = styled.div`
  position: absolute;
  top: calc(-0.625 * var(--fr));
  left: 50%;
  transform: translateX(-50%);
`;

const PlanDetails = ({
  price,
  interval,
  benefits,
  exclusions,
  link,
  trackingTitle,
  imageTitle,
  ctaButtonLabel,
  imageUrl,
  className,
  children,
  planTag,
  onClick,
}) => {
  return (
    <PlanDetailsContainer
      className={className}
      hasChildren={!!children}
      to={link}
      openInNewTab
      onClick={() => onClick(trackingTitle)}
      aria-label=""
    >
      {planTag && <PlanTag>{planTag}</PlanTag>}
      <PlanImage imageUrl={imageUrl}>{imageTitle}</PlanImage>
      <PlanInfo>
        <PlanPrice>
          <FormattedMessage
            id="index.plan-details.price.trial"
            defaultMessage="{price} for 14 days"
            values={{
              price: <Price price={0} />,
            }}
          />
          <div>
            <FormattedMessage
              id="index.plan-details.price"
              defaultMessage="then {price}"
              values={{
                price: <Price price={price} interval={interval} />,
              }}
            />
            *
          </div>
        </PlanPrice>
        <PlanObjectives>
          {benefits.map(benefit => (
            <li key={benefit.key} className="benefit">
              {benefit.value}
            </li>
          ))}
          {exclusions &&
            exclusions.map(exclusion => (
              <li key={exclusion.key} className="exclusion">
                {exclusion.value}
              </li>
            ))}
        </PlanObjectives>
      </PlanInfo>
      <PlanButton>
        <CtaButton
          color="white"
          link={link}
          openInNewTab
          trackingData={{
            title: trackingTitle,
            from: 'Plans Section',
          }}
          fullWidth
        >
          {ctaButtonLabel}
        </CtaButton>
      </PlanButton>
    </PlanDetailsContainer>
  );
};

PlanDetails.propTypes = {
  imageTitle: PropTypes.node.isRequired,
  price: PropTypes.number.isRequired,
  interval: PropTypes.string.isRequired,
  benefits: PropTypes.array.isRequired,
  exclusions: PropTypes.array,
  link: PropTypes.string.isRequired,
  trackingTitle: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  ctaButtonLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
  planTag: PropTypes.node,
  onClick: PropTypes.func.isRequired,
};

export default PlanDetails;



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/plans/PlanDetails.js